import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/accounting/databox`: data messages Hub sent and loaded. Listing the
 * received ones delivers what waits there, so it is a confirmed click.
 */

type Message = {
  id: string;
  dmId: string;
  direction: "sent" | "received";
  senderBoxId: string | null;
  senderName: string | null;
  recipientBoxId: string | null;
  recipientName: string | null;
  annotation: string | null;
  messageStatus: number | null;
  statusText: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  acceptedAt: string | null;
  createdAt: string;
  hasSigned: boolean;
  files: Array<{ name: string; mimeType: string; size: number }>;
  filingPart: "dphdp3" | "dphkh1" | null;
  vatReturn: { id: string; year: number; period: number; quarterly: boolean } | null;
  sentBy: { id: string; name: string | null } | null;
};

type Connection = {
  environment: "production" | "test";
  boxId: string | null;
  ownerName: string | null;
  autoFetch: boolean;
  lastFetchedAt: string | null;
  lastError: string | null;
} | null;

type FetchResult = {
  received: { listed: number; added: number; pending: number } | null;
  sent: { listed: number; added: number; updated: number };
};

export const Route = createFileRoute("/_app/accounting/databox")({
  loader: async () => {
    try {
      const [messages, connection] = await Promise.all([
        api.get<{ messages: Message[] }>("/api/accounting/databox/messages"),
        api.get<{ connection: Connection }>("/api/accounting/databox/connection"),
      ]);
      return { messages: messages.messages, connection: connection.connection };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: DataBoxPage,
});

function size(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} kB`;
}

function DataBoxPage() {
  const { messages, connection } = Route.useLoaderData();
  const router = useRouter();
  const [direction, setDirection] = useState<"received" | "sent">("received");
  const [result, setResult] = useState<FetchResult | null>(null);

  const load = useApiMutation(
    (received: boolean) => api.post<FetchResult>("/api/accounting/databox/fetch", { received }),
    {
      onSuccess: async (res) => {
        setResult(res);
        await router.invalidate();
      },
      fallbackMessage: "Couldn't load the messages.",
    },
  );

  const shown = messages.filter((m) => m.direction === direction);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Data box</h1>
          <p className="text-muted-foreground text-sm">
            {connection?.boxId
              ? `${connection.ownerName ?? ""} (${connection.boxId})`
              : "Not connected yet, see the accounting settings."}
            {connection?.environment === "test" && " · test environment"}
          </p>
        </div>
        {connection && (
          <div className="flex gap-2">
            <Button variant="outline" disabled={load.pending} onClick={() => load.mutate(false)}>
              Refresh sent
            </Button>
            <Button
              disabled={load.pending}
              onClick={() => {
                if (
                  window.confirm(
                    "Every message waiting in the data box counts as delivered from this moment, and its deadlines start running. Load the received messages?",
                  )
                ) {
                  void load.mutate(true);
                }
              }}
            >
              {load.pending ? "Loading…" : "Load messages"}
            </Button>
          </div>
        )}
      </div>

      {connection && (
        <p className="text-muted-foreground text-sm">
          Loading received messages delivers every message waiting in the box (doručení
          přihlášením). Refreshing sent messages delivers nothing.{" "}
          {connection.autoFetch
            ? "Automatic loading is on: Hub loads received messages every 6 hours."
            : "Automatic loading is off: received messages load only on a click."}
          {connection.lastFetchedAt && (
            <>
              {" "}
              Last loaded <LocalDate value={connection.lastFetchedAt} />.
            </>
          )}
        </p>
      )}
      {load.error && <p className="text-destructive text-sm">{load.error}</p>}
      {connection?.lastError && !load.error && (
        <p className="text-destructive text-sm">Last error: {connection.lastError}</p>
      )}
      {result && (
        <p className="text-sm">
          {result.received &&
            `Received: ${result.received.added} new of ${result.received.listed}${result.received.pending ? `, ${result.received.pending} not readable by this account` : ""}. `}
          Sent: {result.sent.added} new, {result.sent.updated} updated.
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Messages</CardTitle>
          <CardDescription>
            Attachments are kept in Hub's archive; the data box deletes messages 90 days after
            delivery.
          </CardDescription>
          <div className="flex gap-2 pt-2">
            {(["received", "sent"] as const).map((d) => (
              <Button
                key={d}
                size="sm"
                variant={direction === d ? "default" : "outline"}
                onClick={() => setDirection(d)}
              >
                {d === "received" ? "Received" : "Sent"}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {shown.length === 0 ? (
            <p className="text-muted-foreground text-sm">No messages.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">ID</th>
                  <th className="py-1 pr-4 font-medium">
                    {direction === "received" ? "From" : "To"}
                  </th>
                  <th className="py-1 pr-4 font-medium">Subject</th>
                  <th className="py-1 pr-4 font-medium">State</th>
                  <th className="py-1 pr-4 font-medium">Delivered</th>
                  <th className="py-1 font-medium">Files</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((m) => (
                  <tr key={m.id} className="border-t align-top">
                    <td className="py-1 pr-4 font-mono text-xs">{m.dmId}</td>
                    <td className="py-1 pr-4">
                      {direction === "received" ? m.senderName : m.recipientName}
                      <div className="font-mono text-muted-foreground text-xs">
                        {direction === "received" ? m.senderBoxId : m.recipientBoxId}
                      </div>
                    </td>
                    <td className="py-1 pr-4">
                      {m.annotation}
                      {m.vatReturn && (
                        <div className="text-muted-foreground text-xs">
                          <Link
                            to="/accounting/vat"
                            search={{
                              year: m.vatReturn.year,
                              period: m.vatReturn.period,
                              quarterly: m.vatReturn.quarterly || undefined,
                            }}
                            className="underline"
                          >
                            VAT {m.vatReturn.period}/{m.vatReturn.year}
                          </Link>
                          {m.sentBy?.name && ` · sent by ${m.sentBy.name}`}
                        </div>
                      )}
                    </td>
                    <td className="py-1 pr-4">
                      {m.statusText && (
                        <Badge variant={m.acceptedAt ? "success" : "outline"}>{m.statusText}</Badge>
                      )}
                    </td>
                    <td className="py-1 pr-4 text-xs">
                      {m.sentAt && (
                        <div>
                          sent <LocalDate value={m.sentAt} />
                        </div>
                      )}
                      {m.deliveredAt && (
                        <div>
                          to the box <LocalDate value={m.deliveredAt} />
                        </div>
                      )}
                      {m.acceptedAt && (
                        <div>
                          accepted <LocalDate value={m.acceptedAt} />
                        </div>
                      )}
                    </td>
                    <td className="space-y-1 py-1">
                      {m.files.map((f, n) => (
                        <div key={`${m.id}-${f.name}`}>
                          <a
                            className="underline"
                            href={`/api/accounting/databox/messages/${m.id}/files/${n}`}
                          >
                            {f.name}
                          </a>{" "}
                          <span className="text-muted-foreground text-xs">{size(f.size)}</span>
                        </div>
                      ))}
                      {m.hasSigned && (
                        <a
                          className="text-xs underline"
                          href={`/api/accounting/databox/messages/${m.id}/files/signed`}
                        >
                          signed message (ZFO)
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
